/**
 * Tour styles - CSS constants
 */

export const TOUR_STYLES_ID = 'app-tour-styles'

// Font family of the design system (`--vn-font-sans`). The tour CSS is a raw
// string shared with extracted packages, so it names the CSS variables
// directly, each with a fallback for a host that does not define them.
const FONT_FAMILY =
    "var(--vn-font-sans, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif)"

/**
 * Tour CSS styles
 */
export const TOUR_CSS = `
    /*
     * Design-system colours, each with a fallback for a host that does not
     * define the variable, declared once here and used below as --_name.
     */
    .tour-popover-container,
    .driver-popover {
        --_accent-solid: var(--vn-accent-solid, #fb923c);
        --_bg-active: var(--vn-bg-active, #e5e7eb);
        --_bg-hover: var(--vn-bg-hover, #f3f4f6);
        --_bg-subtle: var(--vn-bg-subtle, #f9fafb);
        --_bg-surface: var(--vn-bg-surface, #fff);
        --_border-default: var(--vn-border-default, #e5e7eb);
        --_border-strong: var(--vn-border-strong, #c8cdd4);
        --_danger-solid: var(--vn-danger-solid, #dc2626);
        --_fg-default: var(--vn-fg-default, #23272f);
        --_fg-muted: var(--vn-fg-muted, #6b7280);
        --_fg-on-solid: var(--vn-fg-on-solid, #fff);
        --_fg-subtle: var(--vn-fg-subtle, #9ca3af);
        --_focus-ring: var(--vn-focus-ring, #1a6bff);
        --_primary-fg: var(--vn-primary-fg, #fff);
        --_primary-solid: var(--vn-primary-solid, #0051c3);
        --_primary-solid-active: var(--vn-primary-solid-active, #003682);
        --_primary-solid-hover: var(--vn-primary-solid-hover, #0043a3);
        --_success-solid: var(--vn-success-solid, #16a34a);
    }

    /*
     * Backdrop. The target is NOT raised above it (a z-index cannot give a
     * transparent target a background: the dimmed page showed through it, and
     * a stacking-context ancestor traps it entirely). Instead the spotlight
     * is a hole in the backdrop: a fixed box over the target whose huge
     * box-shadow draws the dimming everywhere else. Nothing on the page is
     * moved, restyled or re-parented.
     */
    .tour-overlay {
        position: fixed !important;
        inset: 0 !important;
        overflow: hidden !important;
        z-index: 99999 !important;
        pointer-events: none !important;
        background: rgba(0, 0, 0, 0.6) !important;
    }

    /* A target is being tracked: the spotlight draws the dimming instead */
    .tour-overlay[data-spotlight="on"] {
        background: transparent !important;
    }

    .tour-spotlight {
        position: absolute;
        display: none;
        box-sizing: border-box;
        border-radius: 8px;
        box-shadow: 0 0 0 4px var(--vn-bg-surface, white), 0 0 0 200vmax rgba(0, 0, 0, 0.6);
        pointer-events: none;
    }

    .tour-overlay[data-spotlight="on"] .tour-spotlight {
        display: block;
    }

    /* Marker class only: the highlighted element keeps its own layout and
       paint. The spotlight follows its box. */
    .tour-highlight-element {
        scroll-margin: 96px;
    }

    /* Popover container */
    .tour-popover-container {
        position: fixed;
        background: var(--_bg-surface);
        border-radius: var(--vn-radius-lg, 8px);
        padding: 20px;
        width: 380px;
        max-width: calc(100vw - 40px);
        /* long copy (English, demos) scrolls inside instead of leaving the viewport */
        max-height: calc(100vh - 20px);
        overflow-y: auto;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05);
        z-index: 100001;
        font-family: ${FONT_FAMILY};
        animation: tour-popover-in 0.3s ease-out;
    }

    @keyframes tour-popover-in {
        from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    /* Title */
    .tour-popover-container h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 600;
        color: var(--_fg-default);
        font-family: ${FONT_FAMILY};
        line-height: 1.4;
    }

    /* Description */
    .tour-popover-container p {
        margin: 0 0 16px 0;
        font-size: 14px;
        color: var(--vn-fg-muted);
        line-height: 1.6;
        font-family: ${FONT_FAMILY};
        white-space: pre-line;
    }

    /* Close button: the design system's ghost icon button, size sm */
    .tour-close {
        position: absolute;
        top: 12px;
        right: 12px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--vn-control-height-sm, 28px);
        height: var(--vn-control-height-sm, 28px);
        padding: 0;
        border: 1px solid transparent;
        border-radius: var(--vn-radius-md, 6px);
        background: transparent;
        color: var(--_fg-muted);
        font-family: ${FONT_FAMILY};
        font-size: 20px;
        line-height: 1;
        cursor: pointer;
        transition:
            background-color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease),
            color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease);
    }

    .tour-close:hover {
        background: var(--_bg-hover);
        color: var(--_fg-default);
    }

    .tour-close:active {
        background: var(--_bg-active);
    }

    .tour-close:focus-visible {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 1px;
    }

    /*
     * Navigation buttons: the design system Button, size sm (same height,
     * padding, radius, font and focus ring as its CSS module). No gradient,
     * no coloured shadow. Next / Finish = primary, Previous and the
     * "Open page" action = secondary.
     */
    .tour-btn {
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--vn-space-2, 0.5rem);
        height: var(--vn-control-height-sm, 28px);
        padding: 0 var(--vn-space-3, 0.75rem);
        border: 1px solid transparent;
        border-radius: var(--vn-radius-md, 6px);
        font-family: ${FONT_FAMILY};
        font-size: var(--vn-font-size-sm, 0.75rem);
        font-weight: var(--vn-font-weight-medium, 500);
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;
        transition:
            background-color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease),
            border-color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease),
            color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease);
    }

    .tour-btn:focus-visible {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 1px;
    }

    .tour-btn:disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    .tour-btn-prev,
    .tour-btn-action {
        background: var(--_bg-surface);
        border-color: var(--_border-strong);
        color: var(--_fg-default);
    }

    .tour-btn-prev:hover,
    .tour-btn-action:hover {
        background: var(--_bg-hover);
    }

    .tour-btn-prev:active,
    .tour-btn-action:active {
        background: var(--_bg-active);
    }

    .tour-btn-next {
        background: var(--_primary-solid);
        border-color: var(--_primary-solid);
        color: var(--_primary-fg);
    }

    .tour-btn-next:hover {
        background: var(--_primary-solid-hover);
        border-color: var(--_primary-solid-hover);
    }

    .tour-btn-next:active {
        background: var(--_primary-solid-active);
    }

    /* Step counter */
    .tour-progress {
        font-size: var(--vn-font-size-sm, 0.75rem);
        color: var(--_fg-muted);
        font-family: ${FONT_FAMILY};
    }

    /* Keyboard hint */
    .tour-keyboard-hint {
        font-size: var(--vn-font-size-xs, 0.6875rem);
        color: var(--_fg-subtle);
        font-family: ${FONT_FAMILY};
        margin-top: 2px;
    }

    .tour-keyboard-hint kbd {
        display: inline-block;
        padding: 0 4px;
        font-size: var(--vn-font-size-xs, 0.6875rem);
        line-height: 1.5;
        font-family: ${FONT_FAMILY};
        color: var(--_fg-muted);
        background: var(--_bg-subtle);
        border: 1px solid var(--_border-default);
        border-radius: var(--vn-radius-sm, 3px);
        margin: 0 1px;
    }

    /* Waiting for a page: spinner shown in place of the description */
    .tour-loading {
        display: flex;
        justify-content: center;
        padding: 12px 0 20px;
    }

    .tour-spinner {
        width: 20px;
        height: 20px;
        box-sizing: border-box;
        border: 2px solid var(--_border-default);
        border-top-color: var(--_primary-solid);
        border-radius: 50%;
        animation: tour-spin 0.8s linear infinite;
    }

    @keyframes tour-spin {
        to { transform: rotate(360deg); }
    }

    /* Footer */
    .tour-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 4px;
    }

    .tour-footer-buttons {
        display: flex;
        gap: 8px;
    }

    /* Demo animations container */
    .tour-demo {
        margin: 12px 0 16px 0;
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 80px;
        position: relative;
        overflow: hidden;
    }

    /* Cursor animation */
    .tour-demo-cursor {
        position: absolute;
        width: 20px;
        height: 20px;
        pointer-events: none;
    }

    /* Contribute demo animation */
    .tour-demo-contribute {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .tour-demo-plus-btn {
        width: 32px;
        height: 32px;
        background: var(--_accent-solid);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--_fg-on-solid);
        animation: tour-plus-pulse 1.5s ease-in-out infinite;
    }

    @keyframes tour-plus-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.15); }
    }

    .tour-demo-form {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        animation: tour-form-appear 2s ease-in-out infinite;
        opacity: 0;
    }

    @keyframes tour-form-appear {
        0%, 30% { opacity: 0; transform: translateX(-10px); }
        50%, 80% { opacity: 1; transform: translateX(0); }
        100% { opacity: 0; transform: translateX(-10px); }
    }

    .tour-demo-form-input {
        width: 100px;
        height: 8px;
        background: #f3f4f6;
        border-radius: 4px;
    }

    .tour-demo-form-input.long {
        width: 80px;
        height: 16px;
    }

    .tour-demo-form-btn {
        width: 40px;
        height: 16px;
        background: var(--_primary-solid);
        border-radius: var(--vn-radius-md, 6px);
        align-self: flex-end;
    }

    /* Modal demo shared styles */
    .tour-demo-modal {
        background: var(--_bg-surface);
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        overflow: hidden;
        animation: tour-modal-appear 2.5s ease-in-out infinite;
        width: 200px;
    }

    @keyframes tour-modal-appear {
        0%, 20% { opacity: 0; transform: scale(0.9) translateY(10px); }
        35%, 85% { opacity: 1; transform: scale(1) translateY(0); }
        100% { opacity: 0; transform: scale(0.9) translateY(10px); }
    }

    .tour-demo-modal-header {
        padding: 8px 10px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .tour-demo-modal-title {
        font-size: 11px;
        font-weight: 600;
        color: #1f2937;
    }

    .tour-demo-modal-close {
        width: 14px;
        height: 14px;
        color: #9ca3af;
    }

    .tour-demo-modal-body {
        padding: 10px;
    }

    .tour-demo-modal-row {
        margin-bottom: 8px;
    }

    .tour-demo-modal-label {
        font-size: 9px;
        color: var(--vn-fg-muted);
        margin-bottom: 3px;
        display: block;
    }

    .tour-demo-modal-input {
        width: 100%;
        height: 20px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 9px;
        color: #374151;
        padding: 0 6px;
        display: flex;
        align-items: center;
    }

    .tour-demo-modal-input.select {
        justify-content: space-between;
    }

    .tour-demo-modal-input.select::after {
        content: '▼';
        font-size: 6px;
        color: #9ca3af;
    }

    .tour-demo-modal-textarea {
        width: 100%;
        height: 28px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 8px;
        color: #9ca3af;
        padding: 4px 6px;
    }

    .tour-demo-modal-footer {
        padding: 8px 10px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        gap: 6px;
    }

    .tour-demo-modal-btn {
        padding: 4px 10px;
        border-radius: var(--vn-radius-md, 6px);
        font-size: 9px;
        font-weight: var(--vn-font-weight-medium, 500);
    }

    .tour-demo-modal-btn.cancel {
        background: var(--_bg-surface);
        border: 1px solid var(--_border-strong);
        color: var(--_fg-default);
    }

    .tour-demo-modal-btn.danger {
        background: var(--_danger-solid);
        color: white;
        border: none;
    }

    .tour-demo-modal-btn.success {
        background: var(--_success-solid);
        color: white;
        border: none;
    }

    .tour-demo-modal-btn.primary {
        background: var(--_primary-solid);
        color: white;
        border: none;
    }

    .tour-demo-modal-btn.purple {
        background: var(--_primary-solid);
        color: white;
        border: none;
    }

    /* Delete modal specific */
    .tour-demo-modal.delete .tour-demo-modal-header {
        background: #fef2f2;
    }

    .tour-demo-modal.delete .tour-demo-modal-title {
        color: #dc2626;
    }

    /* Pay modal specific */
    .tour-demo-modal.pay .tour-demo-modal-header {
        background: #ecfdf5;
    }

    .tour-demo-modal.pay .tour-demo-modal-title {
        color: #059669;
    }

    /* Mail modal specific */
    .tour-demo-modal.mail .tour-demo-modal-header {
        background: #eff6ff;
    }

    .tour-demo-modal.mail .tour-demo-modal-title {
        color: #2563eb;
    }

    /* Update modal specific */
    .tour-demo-modal.update .tour-demo-modal-header {
        background: #f5f3ff;
    }

    .tour-demo-modal.update .tour-demo-modal-title {
        color: #7c3aed;
    }

    /* Icon in header */
    .tour-demo-modal-icon {
        width: 14px;
        height: 14px;
        margin-right: 6px;
    }

    .tour-demo-header-left {
        display: flex;
        align-items: center;
    }

    /* Checkbox style for modal */
    .tour-demo-modal-checkbox {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 8px;
        color: var(--vn-fg-muted);
    }

    .tour-demo-modal-checkbox-box {
        width: 10px;
        height: 10px;
        border: 1px solid #d1d5db;
        border-radius: 2px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .tour-demo-modal-checkbox-box.checked {
        background: #3b82f6;
        border-color: #3b82f6;
    }

    .tour-demo-modal-checkbox-box.checked::after {
        content: '✓';
        font-size: 7px;
        color: white;
    }

    /* Add Invoice Line modal - larger modal */
    .tour-demo-modal.invoice-line {
        width: 280px;
    }

    .tour-demo-modal.invoice-line .tour-demo-modal-header {
        background: var(--_primary-solid);
    }

    .tour-demo-modal.invoice-line .tour-demo-modal-title {
        color: white;
    }

    .tour-demo-modal.invoice-line .tour-demo-modal-close {
        color: rgba(255,255,255,0.7);
    }

    .tour-demo-modal-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
    }

    .tour-demo-modal-grid .full-width {
        grid-column: 1 / -1;
    }

    .tour-demo-modal-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 8px;
        background: #f3f4f6;
        border-radius: 4px;
        font-size: 8px;
        color: #374151;
    }

    .tour-demo-modal-chip.active {
        background: #dbeafe;
        color: #1d4ed8;
    }

    .tour-demo-modal-service {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 6px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 8px;
        color: #374151;
    }

    .tour-demo-modal-service-close {
        width: 10px;
        height: 10px;
        color: #9ca3af;
    }

    /* Preview Mail demo - skeleton style */
    .tour-demo-email {
        background: var(--_bg-surface);
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        overflow: hidden;
        width: 240px;
    }

    .tour-demo-email-header {
        padding: 10px 12px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .tour-demo-email-logo {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .tour-demo-email-logo-icon {
        width: 20px;
        height: 20px;
        background: var(--_danger-solid);
        border-radius: 50%;
    }

    .tour-demo-email-logo-text {
        width: 50px;
        height: 10px;
        background: #1A237E;
        border-radius: 2px;
    }

    .tour-demo-email-support {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 3px;
    }

    .tour-demo-skeleton {
        background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
        background-size: 200% 100%;
        animation: tour-skeleton-pulse 1.5s ease-in-out infinite;
        border-radius: 3px;
    }

    @keyframes tour-skeleton-pulse {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }

    .tour-demo-email-body {
        padding: 12px;
    }

    .tour-demo-email-title {
        height: 12px;
        width: 180px;
        margin-bottom: 10px;
        background: #dbeafe;
        border-radius: 3px;
    }

    .tour-demo-email-greeting {
        height: 8px;
        width: 120px;
        margin-bottom: 8px;
    }

    .tour-demo-email-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-bottom: 12px;
    }

    .tour-demo-email-content-line {
        height: 6px;
        border-radius: 2px;
    }

    .tour-demo-email-table {
        width: 100%;
        margin-bottom: 12px;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        overflow: hidden;
    }

    .tour-demo-email-table-header {
        display: grid;
        grid-template-columns: 20px 1fr 50px 50px;
        gap: 4px;
        padding: 6px 8px;
        background: #f3f4f6;
        border-bottom: 1px solid #e5e7eb;
    }

    .tour-demo-email-table-header-cell {
        height: 8px;
        background: #d1d5db;
        border-radius: 2px;
    }

    .tour-demo-email-table-row {
        display: grid;
        grid-template-columns: 20px 1fr 50px 50px;
        gap: 4px;
        padding: 6px 8px;
        border-bottom: 1px solid #f3f4f6;
    }

    .tour-demo-email-table-cell {
        height: 6px;
        border-radius: 2px;
    }

    .tour-demo-email-table-total {
        display: flex;
        justify-content: space-between;
        padding: 6px 8px;
        background: #f8fafc;
    }

    .tour-demo-email-table-total-label {
        height: 8px;
        width: 60px;
        background: #1A237E;
        border-radius: 2px;
    }

    .tour-demo-email-table-total-value {
        height: 8px;
        width: 50px;
        background: #1A237E;
        border-radius: 2px;
    }

    .tour-demo-email-payment {
        background: #f9fafb;
        padding: 8px;
        border-radius: 4px;
        margin-bottom: 10px;
    }

    .tour-demo-email-payment-title {
        height: 8px;
        width: 100px;
        background: #374151;
        border-radius: 2px;
        margin-bottom: 6px;
    }

    .tour-demo-email-payment-lines {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .tour-demo-email-footer {
        padding: 8px 12px;
        border-top: 3px solid #dc2626;
        background: #f9fafb;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .tour-demo-email-download {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 50px;
        height: 18px;
        background: #1A237E;
        border-radius: 4px;
    }

    /* Mobile responsive styles */
    @media (max-width: 767px) {
        .tour-popover-container {
            padding: 16px;
            width: calc(100vw - 20px);
            max-width: calc(100vw - 20px);
        }

        .tour-popover-container h3 {
            font-size: 15px;
            margin-bottom: 6px;
        }

        .tour-popover-container p {
            font-size: 13px;
            line-height: 1.5;
        }

        .tour-btn {
            padding: 7px 14px;
            font-size: 13px;
        }

        .tour-footer {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
        }

        .tour-footer-buttons {
            width: 100%;
            justify-content: flex-end;
        }

        .tour-keyboard-hint {
            display: none; /* Hide keyboard hints on mobile */
        }

        .tour-demo {
            margin: 8px 0 12px 0;
            padding: 12px;
        }
    }

    /*
     * Release tours (driver.js: TourProvider, useTourLauncher) get the same
     * chrome as the tours above. driver.css styles its buttons with
     * \`.driver-popover-footer button\` (0,1,1); these selectors are one class
     * heavier so they win without !important.
     */
    .driver-popover {
        font-family: ${FONT_FAMILY};
        background: var(--_bg-surface);
        color: var(--_fg-default);
        border-radius: var(--vn-radius-lg, 8px);
    }

    .driver-popover .driver-popover-title {
        font-family: ${FONT_FAMILY};
        font-size: 16px;
        font-weight: 600;
        color: var(--_fg-default);
    }

    .driver-popover .driver-popover-description {
        font-family: ${FONT_FAMILY};
        font-size: 14px;
        line-height: 1.6;
        color: var(--_fg-muted);
    }

    .driver-popover .driver-popover-progress-text {
        font-family: ${FONT_FAMILY};
        font-size: var(--vn-font-size-sm, 0.75rem);
        color: var(--_fg-muted);
    }

    .driver-popover .driver-popover-footer button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: var(--vn-control-height-sm, 28px);
        padding: 0 var(--vn-space-3, 0.75rem);
        border: 1px solid var(--_border-strong);
        border-radius: var(--vn-radius-md, 6px);
        background: var(--_bg-surface);
        color: var(--_fg-default);
        font-family: ${FONT_FAMILY};
        font-size: var(--vn-font-size-sm, 0.75rem);
        font-weight: var(--vn-font-weight-medium, 500);
        line-height: 1;
        text-shadow: none;
        white-space: nowrap;
        transition:
            background-color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease),
            border-color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease),
            color var(--vn-duration-fast, 120ms) var(--vn-ease-standard, ease);
    }

    .driver-popover .driver-popover-footer button:hover,
    .driver-popover .driver-popover-footer button:focus {
        background: var(--_bg-hover);
    }

    .driver-popover .driver-popover-footer button:focus-visible {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 1px;
    }

    .driver-popover .driver-popover-footer .driver-popover-next-btn {
        background: var(--_primary-solid);
        border-color: var(--_primary-solid);
        color: var(--_primary-fg);
    }

    .driver-popover .driver-popover-footer .driver-popover-next-btn:hover,
    .driver-popover .driver-popover-footer .driver-popover-next-btn:focus {
        background: var(--_primary-solid-hover);
        border-color: var(--_primary-solid-hover);
    }

    .driver-popover .driver-popover-footer .driver-popover-btn-disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    .driver-popover .driver-popover-close-btn {
        width: var(--vn-control-height-sm, 28px);
        height: var(--vn-control-height-sm, 28px);
        top: 8px;
        right: 8px;
        border-radius: var(--vn-radius-md, 6px);
        color: var(--_fg-muted);
        line-height: var(--vn-control-height-sm, 28px);
    }

    .driver-popover .driver-popover-close-btn:hover,
    .driver-popover .driver-popover-close-btn:focus {
        background: var(--_bg-hover);
        color: var(--_fg-default);
    }

    .driver-popover .driver-popover-close-btn:focus-visible {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 1px;
    }
`
